import type { LifecycleModule } from "./modules/lifecycle";

type ReleasedMedia = {
  src: string;
  currentTime: number;
  resume: boolean;
};

let installed = false;

/**
 * Detaches the source of every media element the game has played while the document is hidden,
 * and restores it when the document is shown again.
 *
 * Mobile browsers keep a media player card on the lock screen or notification tray for as long
 * as a media element holds a source, even when paused. Unity plays large compressed clips through
 * detached `<audio>` elements, so the card outlives the game session. Detaching the source ends
 * the browser's media session; a `MediaElementAudioSourceNode` stays bound to the element, so
 * Web Audio routing survives the round trip.
 *
 * Skipped on iOS, where detaching never clears the Now Playing card; the platform switches the
 * page's audio session to `ambient` instead.
 */
export function releaseMediaWhileHidden(lifecycle: LifecycleModule): void {
  if (installed || typeof HTMLMediaElement === "undefined" || isIos()) {
    return;
  }
  installed = true;

  const played = new Set<HTMLMediaElement>();
  const released = new Map<HTMLMediaElement, ReleasedMedia>();

  const restore = (element: HTMLMediaElement) => {
    const saved = released.get(element);
    released.delete(element);
    // The game assigned a new source while released; its choice wins.
    if (!saved || element.getAttribute("src") !== null) {
      return false;
    }
    element.src = saved.src;
    element.currentTime = saved.currentTime;
    return saved.resume;
  };

  const { prototype } = HTMLMediaElement;
  const originalPlay = Object.getOwnPropertyDescriptor(prototype, "play")
    ?.value as (this: HTMLMediaElement) => Promise<void>;
  prototype.play = function (this: HTMLMediaElement) {
    played.add(this);
    restore(this);
    return originalPlay.call(this);
  };

  lifecycle.subscribe("hide", () => {
    for (const element of played) {
      const src = element.getAttribute("src");
      if (!src) {
        played.delete(element);
        continue;
      }
      released.set(element, {
        src,
        currentTime: element.currentTime,
        resume: !element.paused,
      });
      element.pause();
      element.removeAttribute("src");
      element.load();
    }
  });

  lifecycle.subscribe("show", () => {
    for (const element of released.keys()) {
      if (restore(element)) {
        originalPlay.call(element).catch(() => undefined);
      }
    }
  });
}

function isIos(): boolean {
  const { userAgent, maxTouchPoints } = navigator;
  // iPadOS reports a desktop Safari user agent.
  return (
    /iPhone|iPad|iPod/.test(userAgent) ||
    (userAgent.includes("Macintosh") && maxTouchPoints > 1)
  );
}
